// Killer Sudoku: a Sudoku whose grid is also cut into cages; the digits in a cage add up to its sum and don't repeat.
// Puzzles are made offline (script/generate-killer.ts) from solved grids: cut the grid into small connected cages,
// then keep the puzzle only if this solver proves exactly one solution, adding given digits on the easier levels.
// Levels: Easy (cages plus a few givens), Medium (fewer givens), Hard (no givens).
import { COL, PEERS, ROW } from "./core"
import { seeded, shuffle } from "../lib/rng"

export type KillerLevel = "easy" | "medium" | "hard"
export const KILLER_LEVELS: KillerLevel[] = ["easy", "medium", "hard"]
/** A cage: its sum, then its cells (indices 0-80). */
export type Cage = number[]
export interface KillerPuzzle { id: string; level: KillerLevel; p: string; s: string; c: Cage[] }

const bit = (d: number): number => 1 << (d - 1)
const popcount = (m: number): number => { let n = 0; for (; m; m &= m - 1) n++; return n }

/** The least and most that `k` distinct digits from `free` (a bitmask) can add up to. */
function range(free: number, k: number): [number, number] {
  const ds = [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((d) => free & bit(d))
  if (ds.length < k) return [Infinity, -Infinity]
  let lo = 0, hi = 0
  for (let i = 0; i < k; i++) { lo += ds[i]!; hi += ds[ds.length - 1 - i]! }
  return [lo, hi]
}

/** How many solutions (stopping at `limit`), with cage sums and no repeats in a cage, from `grid` (0 = empty). */
export function countKiller(cages: Cage[], grid: number[], limit = 2): number {
  const g = grid.slice()
  const cageOf = new Array<number>(81).fill(-1)
  cages.forEach((cg, k) => cg.slice(1).forEach((i) => { cageOf[i] = k }))
  let count = 0
  const candidates = (i: number): number => {
    let m = 0b111111111
    for (const p of PEERS[i]!) if (g[p]) m &= ~bit(g[p]!)
    const cg = cages[cageOf[i]!]
    if (!cg) return m
    const cells = cg.slice(1)
    let used = 0, sum = 0, empty = 0
    for (const j of cells) { if (g[j]) { used |= bit(g[j]!); sum += g[j]! } else empty++ }
    m &= ~used
    const target = cg[0]!
    let out = 0
    for (let d = 1; d <= 9; d++) {
      if (!(m & bit(d))) continue
      const rest = target - sum - d
      if (empty === 1) { if (rest === 0) out |= bit(d); continue }
      const [lo, hi] = range(0b111111111 & ~used & ~bit(d), empty - 1)
      if (rest >= lo && rest <= hi) out |= bit(d)
    }
    return out
  }
  const go = (): void => {
    if (count >= limit) return
    let best = -1, bestM = 0, bestN = 10
    for (let i = 0; i < 81; i++) {
      if (g[i]) continue
      const m = candidates(i)
      const n = popcount(m)
      if (n === 0) return
      if (n < bestN) { best = i; bestM = m; bestN = n; if (n === 1) break }
    }
    if (best < 0) { count++; return }
    for (let d = 1; d <= 9; d++) {
      if (!(bestM & bit(d))) continue
      g[best] = d
      go()
      g[best] = 0
      if (count >= limit) return
    }
  }
  go()
  return count
}

/** Cuts a solved grid into connected cages of 2 to 4 cells with no repeated digit (and a few single cells). */
export function makeCages(solution: number[], seed: string): Cage[] {
  const rand = seeded(seed)
  const cageOf = new Array<number>(81).fill(-1)
  const cages: number[][] = []
  const order = shuffle(Array.from({ length: 81 }, (_, i) => i), rand)
  const nbrs = (i: number): number[] => [i - 9, i + 9, COL(i) > 0 ? i - 1 : -1, COL(i) < 8 ? i + 1 : -1].filter((j) => j >= 0 && j < 81)
  for (const start of order) {
    if (cageOf[start]! >= 0) continue
    const size = 2 + Math.floor(rand() * 3) // 2, 3 or 4
    const cells = [start]
    cageOf[start] = cages.length
    while (cells.length < size) {
      const options = cells.flatMap(nbrs).filter((j) => cageOf[j]! < 0 && !cells.some((c) => solution[c] === solution[j]))
      if (!options.length) break
      const pick = options[Math.floor(rand() * options.length)]!
      cells.push(pick)
      cageOf[pick] = cages.length
    }
    cages.push(cells)
  }
  return cages.map((cells) => [cells.reduce((n, i) => n + solution[i]!, 0), ...cells.sort((a, b) => a - b)])
}

const GIVENS: Record<KillerLevel, number> = { easy: 14, medium: 6, hard: 0 }

/** One puzzle at `level` from a solved grid, or null if no unique version was found from this seed. Hard needs a
 *  cut with exactly one solution and no givens; Easy and Medium add givens until it's unique, then a few more. */
export function makeKiller(solution: number[], level: KillerLevel, seed: string, tries = 12): KillerPuzzle | null {
  const rand = seeded(`${seed}:givens`)
  for (let t = 0; t < tries; t++) {
    const cages = makeCages(solution, `${seed}:${t}`)
    const grid = new Array<number>(81).fill(0)
    if (level === "hard") {
      if (countKiller(cages, grid) === 1) return pack(seed, level, grid, solution, cages)
      continue
    }
    // Add givens one at a time (spread out) until unique, then top up to the level's count.
    const cells = shuffle(Array.from({ length: 81 }, (_, i) => i), rand)
    let k = 0
    while (countKiller(cages, grid) !== 1 && k < cells.length) { grid[cells[k]!] = solution[cells[k]!]!; k++ }
    for (let extra = grid.filter(Boolean).length; extra < GIVENS[level] && k < cells.length; extra++, k++) grid[cells[k]!] = solution[cells[k]!]!
    if (grid.filter(Boolean).length <= GIVENS[level] + 4) return pack(seed, level, grid, solution, cages)
  }
  return null
}

const pack = (id: string, level: KillerLevel, grid: number[], solution: number[], cages: Cage[]): KillerPuzzle =>
  ({ id, level, p: grid.join(""), s: solution.join(""), c: cages })

/** For the board: each cell's cage, the cage's sum on its first cell, and which sides border another cage. */
export function cageLayout(cages: Cage[]): { cage: number[]; sumAt: Map<number, number>; edges: string[] } {
  const cage = new Array<number>(81).fill(-1)
  const sumAt = new Map<number, number>()
  cages.forEach((cg, k) => { const cells = cg.slice(1); cells.forEach((i) => { cage[i] = k }); sumAt.set(Math.min(...cells), cg[0]!) })
  const edges = Array.from({ length: 81 }, (_, i) => {
    const out: string[] = []
    if (ROW(i) === 0 || cage[i - 9] !== cage[i]) out.push("t")
    if (ROW(i) === 8 || cage[i + 9] !== cage[i]) out.push("b")
    if (COL(i) === 0 || cage[i - 1] !== cage[i]) out.push("l")
    if (COL(i) === 8 || cage[i + 1] !== cage[i]) out.push("r")
    return out.join("")
  })
  return { cage, sumAt, edges }
}
